class BundleSelector extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.config = {
      sizeIndex: -1,
      colorIndex: -1,
      unitPackValue: '',
      colorRowLabel: 'Color for Pack',
    };
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    try {
      const variantsEl = this.querySelector('[data-bundle-variants]');
      const configEl = this.querySelector('[data-bundle-config]');
      if (variantsEl) this.variants = JSON.parse(variantsEl.textContent);
      if (configEl) this.config = { ...this.config, ...JSON.parse(configEl.textContent) };
    } catch (error) {
      console.error('Bundle selector data error', error);
    }

    this.preorderButton = this.querySelector('[data-bundle-preorder]');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.cartDrawer = document.querySelector('drawer-cart');

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);

    this.refreshSelectionState(true);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const disabledSwatch = event.target.closest('.bundle-selector__swatch-wrap');
    if (disabledSwatch) {
      const input = disabledSwatch.querySelector('[data-bundle-color]');
      if (input && (input.disabled || input.classList.contains('disabled'))) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    const button = event.target.closest('[data-bundle-preorder]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    this.handlePreOrder();
  }

  onChange(event) {
    this.clearError();
    this.refreshSelectionState(false);

    if (
      event.target.matches('[data-bundle-pack]') ||
      event.target.matches('[data-bundle-color]')
    ) {
      this.updatePackPrices();
      this.updateCurrentVariant();
    }
  }

  refreshSelectionState(isInitial) {
    this.updateColorRowsVisibility();
    this.updateColorAvailability();
    this.ensureAvailableColorDefaults();
    this.updatePackAvailability();
    this.syncFormProperties();
    if (isInitial) this.updateCurrentVariant(true);
    this.updatePreorderButtonState();
  }

  normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  getSelectedPackInput() {
    return this.querySelector('[data-bundle-pack]:checked');
  }

  getSelectedPack() {
    return this.getSelectedPackInput()?.value || '';
  }

  getSelectedPackQty() {
    const input = this.getSelectedPackInput();
    if (!input) return 1;

    const fromData = parseInt(input.dataset.packQty || '0', 10);
    if (fromData > 0) return fromData;

    const match = String(input.value || '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 1;
  }

  getVisibleColorRows() {
    return Array.from(this.querySelectorAll('[data-color-row]')).filter((row) => !row.hidden);
  }

  getRowColor(row) {
    const checked = this.querySelector(`[data-color-row="${row}"] [data-bundle-color]:checked`);
    return checked ? checked.value : '';
  }

  getSelectedColors() {
    return this.getVisibleColorRows().map((row) => {
      const checked = row.querySelector('[data-bundle-color]:checked');
      return checked ? checked.value : '';
    });
  }

  findVariant(packValue, colorValue) {
    const packNorm = this.normalize(packValue);
    const colorNorm = this.normalize(colorValue);

    return this.variants.find((variant) => {
      const options = variant.options || [variant.option1, variant.option2, variant.option3];

      let sizeMatch = true;
      let colorMatch = true;

      if (this.config.sizeIndex >= 0) {
        sizeMatch = this.normalize(options[this.config.sizeIndex]) === packNorm;
      } else if (packValue) {
        sizeMatch = options.some((option) => this.normalize(option) === packNorm);
      }

      if (this.config.colorIndex >= 0 && colorValue) {
        colorMatch = this.normalize(options[this.config.colorIndex]) === colorNorm;
      } else if (colorValue) {
        colorMatch = options.some((option) => this.normalize(option) === colorNorm);
      }

      return sizeMatch && colorMatch;
    });
  }

  getUnitPrice(colorValue) {
    const unitPack = this.config.unitPackValue;
    if (unitPack) {
      const unitVariant = this.findVariant(unitPack, colorValue) || this.findVariant(unitPack);
      if (unitVariant) return unitVariant.price;
    }

    const oneQtyCard = this.querySelector('[data-pack-qty="1"]');
    if (oneQtyCard) {
      const unitVariant =
        this.findVariant(oneQtyCard.dataset.packValue, colorValue) ||
        this.findVariant(oneQtyCard.dataset.packValue);
      if (unitVariant) return unitVariant.price;
    }

    return this.variants[0]?.price || 0;
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, Shopify.money_format);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  updateColorRowsVisibility() {
    const qty = this.getSelectedPackQty();
    this.querySelectorAll('[data-color-row]').forEach((row) => {
      const rowNum = parseInt(row.dataset.colorRow, 10);
      row.hidden = rowNum > qty;
    });
  }

  updateColorAvailability() {
    const pack = this.getSelectedPack();
    const rows = this.getVisibleColorRows();

    rows.forEach((row) => {
      row.querySelectorAll('[data-bundle-color]').forEach((input) => {
        const variant = this.findVariant(pack, input.value);
        const unavailable = !variant || !variant.available;
        input.classList.toggle('disabled', unavailable);
        input.disabled = unavailable;
        input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        input.closest('.bundle-selector__swatch-wrap')?.classList.toggle('is-disabled', unavailable);
      });
    });
  }

  ensureAvailableColorDefaults() {
    this.getVisibleColorRows().forEach((row) => {
      const availableInputs = Array.from(row.querySelectorAll('[data-bundle-color]:not(:disabled)'));
      if (!availableInputs.length) {
        row.querySelectorAll('[data-bundle-color]').forEach((input) => {
          input.checked = false;
        });
        return;
      }

      const checked = row.querySelector('[data-bundle-color]:checked');
      const checkedIsAvailable = checked && !checked.disabled && !checked.classList.contains('disabled');

      if (!checkedIsAvailable) {
        availableInputs[0].checked = true;
      }
    });
  }

  updatePackAvailability() {
    this.querySelectorAll('[data-pack-card]').forEach((card) => {
      const packValue = card.dataset.packValue;
      const input = card.querySelector('[data-bundle-pack]');
      const hasAvailableColor = this.variants.some((variant) => {
        if (!variant.available) return false;
        const options = variant.options || [variant.option1, variant.option2, variant.option3];
        if (this.config.sizeIndex >= 0) {
          return this.normalize(options[this.config.sizeIndex]) === this.normalize(packValue);
        }
        return options.some((option) => this.normalize(option) === this.normalize(packValue));
      });

      if (input) {
        input.disabled = !hasAvailableColor;
        input.classList.toggle('disabled', !hasAvailableColor);
      }
      card.classList.toggle('is-disabled', !hasAvailableColor);

      if (!hasAvailableColor && input?.checked) {
        const nextPack = this.querySelector('[data-bundle-pack]:not(:disabled)');
        if (nextPack) {
          nextPack.checked = true;
          this.updateColorRowsVisibility();
          this.updateColorAvailability();
          this.ensureAvailableColorDefaults();
        }
      }
    });
  }

  getSelectionValidation() {
    const pack = this.getSelectedPack();
    if (!pack) {
      return { valid: false, error: 'Please select a pack size.', disableButton: true };
    }

    const packInput = this.getSelectedPackInput();
    if (packInput?.disabled) {
      return { valid: false, error: `${pack} is not available.`, disableButton: true };
    }

    const colorRows = this.getVisibleColorRows();
    if (!colorRows.length) {
      const variant = this.findVariant(pack);
      if (!variant || !variant.available) {
        return { valid: false, error: `${pack} is sold out.`, disableButton: true };
      }
      return { valid: true, error: '', disableButton: false };
    }

    for (let index = 0; index < colorRows.length; index += 1) {
      const row = colorRows[index];
      const rowNum = parseInt(row.dataset.colorRow, 10) || index + 1;
      const availableInputs = row.querySelectorAll('[data-bundle-color]:not(:disabled)');
      const checked = row.querySelector('[data-bundle-color]:checked:not(:disabled)');

      if (!availableInputs.length) {
        return {
          valid: false,
          error: `No available colors for ${this.config.colorRowLabel} ${rowNum}.`,
          disableButton: true,
        };
      }

      if (!checked) {
        return {
          valid: false,
          error: `Please choose a color for ${this.config.colorRowLabel} ${rowNum}.`,
          disableButton: true,
        };
      }

      const variant = this.findVariant(pack, checked.value);
      if (!variant || !variant.available) {
        return {
          valid: false,
          error: `${checked.value} / ${pack} is sold out.`,
          disableButton: true,
        };
      }
    }

    return { valid: true, error: '', disableButton: false };
  }

  updatePreorderButtonState() {
    if (!this.preorderButton || this.preorderButton.classList.contains('loadingState')) return;
    const { disableButton } = this.getSelectionValidation();
    this.preorderButton.disabled = disableButton;
    this.preorderButton.setAttribute('aria-disabled', disableButton ? 'true' : 'false');
  }

  updatePackPrices() {
    const color = this.getRowColor(1);
    const unitPrice = this.getUnitPrice(color);

    this.querySelectorAll('[data-pack-card]').forEach((card) => {
      const priceEl = card.querySelector('[data-pack-price]');
      const compareEl = card.querySelector('[data-pack-compare]');
      const fixedPrice = card.dataset.fixedPrice ? parseInt(card.dataset.fixedPrice, 10) : null;
      const fixedCompare = card.dataset.fixedCompare ? parseInt(card.dataset.fixedCompare, 10) : null;

      if (fixedPrice != null) {
        if (priceEl) priceEl.textContent = this.formatMoney(fixedPrice);
        if (compareEl) {
          if (fixedCompare != null && fixedCompare > fixedPrice) {
            compareEl.textContent = this.formatMoney(fixedCompare);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        return;
      }

      const variant = this.findVariant(card.dataset.packValue, color) || this.findVariant(card.dataset.packValue);
      if (!variant) return;

      const qty = parseInt(card.dataset.packQty || '1', 10);
      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

      let compareAt = variant.compare_at_price;
      if ((!compareAt || compareAt <= variant.price) && unitPrice && qty > 1) {
        compareAt = unitPrice * qty;
      }

      if (compareEl) {
        if (compareAt && compareAt > variant.price) {
          compareEl.textContent = this.formatMoney(compareAt);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    });
  }

  updateCurrentVariant(isInitial) {
    const pack = this.getSelectedPack();
    const color = this.getRowColor(1);
    this.currentVariant = this.findVariant(pack, color) || this.findVariant(pack);

    if (!this.currentVariant) {
      this.toggleAddButton(true, window.variantStrings?.unavailable || 'Unavailable');
      return;
    }

    this.updateVariantInput();
    if (!isInitial) {
      this.updateMedia();
      this.updateURL();
      this.renderProductInfo();
    } else {
      this.toggleAddButton(!this.currentVariant.available, window.variantStrings?.soldOut || 'Sold out');
    }
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input || !this.currentVariant) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateMedia() {
    if (!this.currentVariant?.featured_media) return;
    try {
      const carouselElement = this.closest('.main-product')?.querySelector('product-carousel');
      carouselElement?.goToSlideBasedOnMedia?.(this.currentVariant.featured_media.id);
      const mediaItem = document.querySelector(
        `[data-media-target-id="media-${this.currentVariant.featured_media.id}"]`
      );
      mediaItem && mediaItem.parentElement.prepend(mediaItem);
    } catch (error) {
      console.log(error);
    }
  }

  renderProductInfo() {
    if (!this.currentVariant) return;
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button?.querySelector?.('[name="add"]');

    if (this.AbortController) this.AbortController.abort();
    this.AbortController = new AbortController();

    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`, {
      signal: this.AbortController.signal,
    })
      .then((response) => response.text())
      .then((data) => {
        const html = new DOMParser().parseFromString(data, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;

        const inventorySource = html.getElementById(`Inventory-${this.dataset.section}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
        if (inventorySource && inventoryDestination) {
          inventoryDestination.innerHTML = inventorySource.innerHTML;
        }

        this.toggleAddButton(!this.currentVariant.available, window.variantStrings?.soldOut || 'Sold out');
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
      });
  }

  toggleAddButton(disable, text) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] .btn--text');
    if (!addButton) return;

    addButton.disabled = Boolean(disable);
    if (text && addButtonText) addButtonText.textContent = text;
    if (!disable && addButtonText && window.variantStrings?.addToCart) {
      addButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  setPreorderDisabled(disabled) {
    if (!this.preorderButton) return;
    if (this.preorderButton.classList.contains('loadingState')) return;
    this.preorderButton.disabled = Boolean(disabled);
  }

  setPreorderLoading(isLoading) {
    if (!this.preorderButton) return;
    this.preorderButton.classList.toggle('loadingState', Boolean(isLoading));
    if (isLoading) {
      this.preorderButton.disabled = true;
    } else {
      this.preorderButton.classList.remove('completedState');
      this.updatePreorderButtonState();
    }
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  showError(message) {
    if (!this.errorEl) {
      window.alert(message);
      return;
    }
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  buildCartItems() {
    const validation = this.getSelectionValidation();
    if (!validation.valid) {
      return { error: validation.error };
    }

    const pack = this.getSelectedPack();
    const items = [];
    const colorRows = this.getVisibleColorRows();

    if (!colorRows.length) {
      const variant = this.findVariant(pack);
      items.push({
        id: Number(variant.id),
        quantity: 1,
        properties: {
          Pack: pack,
        },
      });
      return { items };
    }

    colorRows.forEach((row, index) => {
      const rowNum = parseInt(row.dataset.colorRow, 10) || index + 1;
      const colorInput = row.querySelector('[data-bundle-color]:checked:not(:disabled)');
      const color = colorInput.value;
      const variant = this.findVariant(pack, color);

      items.push({
        id: Number(variant.id),
        quantity: 1,/* 
        properties: {
          Pack: pack,
          [`${this.config.colorRowLabel} ${rowNum}`]: color,
          'Bundle position': String(rowNum),
        }, */
      });
    });

    return { items };
  }

  getSectionsParam() {
    try {
      if (typeof HELPER_UTIL !== 'undefined') {
        return HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString();
      }
    } catch (error) {
      console.log(error);
    }
    return 'cart-drawer,cart-count-bubble';
  }

  openCartDrawer(sections) {
    const root = window.Shopify?.routes?.root || '/';

    if (sections && typeof HELPER_UTIL !== 'undefined' && typeof _EVENT_HELPER !== 'undefined') {
      window.currentActiveElement = this.preorderButton;
      HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.triggerCartChange, this.preorderButton || document, {
        sections,
      });
      return;
    }

    if (typeof HELPER_UTIL !== 'undefined' && typeof _EVENT_HELPER !== 'undefined') {
      HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart, document, {});
      HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, document, {
        drawerType: 'cart-drawer',
      });
      return;
    }

    window.location.href = `${root}cart`;
  }

  async handlePreOrder() {
    this.clearError();

    const validation = this.getSelectionValidation();
    if (!validation.valid) {
      this.showError(validation.error);
      this.updatePreorderButtonState();
      return;
    }

    const { items, error } = this.buildCartItems();
    if (error) {
      this.showError(error);
      this.updatePreorderButtonState();
      return;
    }

    if (!items.length) {
      this.showError('No items selected to add.');
      return;
    }

    this.setPreorderLoading(true);

    const root = window.Shopify?.routes?.root || '/';
    const sections = this.getSectionsParam();

    try {
      const response = await fetch(`${root}cart/add.js?sections=${encodeURIComponent(sections)}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          data.description ||
          data.message ||
          (typeof data === 'string' ? data : 'Unable to add items to cart.');
        this.showError(message);
        return;
      }

      if (this.preorderButton) this.preorderButton.classList.add('completedState');
      this.openCartDrawer(data.sections);
    } catch (err) {
      console.error('Bundle preorder error', err);
      this.showError('Something went wrong. Please try again.');
    } finally {
      setTimeout(() => {
        this.setPreorderLoading(false);
        if (this.preorderButton) this.preorderButton.classList.remove('completedState');
      }, 800);
    }
  }

  syncFormProperties() {
    const form = document.getElementById(`product-form-${this.dataset.section}`);
    if (!form) return;

    form.querySelectorAll('[data-bundle-property]').forEach((el) => el.remove());

    const packInput = this.getSelectedPackInput();
    if (packInput) {
      this.appendPropertyInput(form, 'Pack', packInput.value);
    }

    this.getSelectedColors().forEach((color, index) => {
      if (!color) return;
      this.appendPropertyInput(form, `${this.config.colorRowLabel} ${index + 1}`, color);
    });
  }

  appendPropertyInput(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    input.setAttribute('data-bundle-property', '');
    form.appendChild(input);
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}
